import "../css/tailwind.css";
import "../scss/main.scss";
import Swup from "swup";
import SwupPreloadPlugin from "@swup/preload-plugin";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}

window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
gsap.registerPlugin(ScrollTrigger);

// Init Application
import Menu from "./components/Menu";
import Scroller from "./components/Scroller";
import Animations from "./components/Animations";

const initPreloader = async () => {
  const preloader = document.querySelector("#preloader");
  if (!preloader) return;

  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  Scroller.stop();

  // Pas de hero (.section-entry) => l'intro (vol du logo vers le header,
  // reveal texte/menu) n'a pas de sens. On retire simplement le voile et on
  // révèle la page normalement.
  if (!document.querySelector(".section-entry")) {
    Scroller.start();
    preloader.remove();
    document.body.classList.add("loaded");
    return;
  }

  const hasHero = !!document.querySelector(".canvas-hero-wrap");
  // En dessous de lg (992px), pas de vol du gros logo : le viewport mobile
  // bouge (barre d'URL) pendant l'attente, ce qui fait sauter le logo au
  // raccord. Le logo header apparaît simplement avec le menu (phase 2).
  const isDesktop = window.matchMedia("(min-width: 992px)").matches;
  const bigLogo = preloader.querySelector(".logo");
  const headLogo = document.querySelector("#header a.logo");
  const menuWrap = document.querySelector("#header .menu-wrap");
  const heroText = document.querySelectorAll(".section-entry .reveal-texte");
  const breadcrumb = document.querySelectorAll(".section-entry .breadcrumb");
  const heroButtons = document.querySelectorAll(".section-entry .btn");

  // On masque le contenu final AVANT l'attente des assets (via GSAP inline,
  // pas de voile CSS lié à .loaded), pour éviter tout flash. La timeline
  // (garantie de se terminer grâce au timeout) les révèle ensuite.
  if (headLogo) gsap.set(headLogo, { opacity: 0 });
  if (menuWrap) gsap.set(menuWrap, { opacity: 0 });
  if (heroText.length) gsap.set(heroText, { opacity: 0 });
  if (breadcrumb.length) gsap.set(breadcrumb, { opacity: 0 });
  if (heroButtons.length) gsap.set(heroButtons, { opacity: 0 });

  // Attente des assets critiques (image hero décodée + polices),
  // avec un garde-fou anti-blocage.
  await Promise.race([
    Promise.all([Animations.heroReady, document.fonts.ready]),
    new Promise((resolve) => setTimeout(resolve, 3500)),
  ]);

  // Chrome restaure la position de scroll autour du load, qui tombe PENDANT
  // l'attente ci-dessus (après les scrollTo de initApp/initPreloader). On
  // ré-affirme donc le retour en haut ici, juste avant que la timeline mesure
  // le header (measureFlip) : sinon le logo vole vers un header hors écran.
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });

  const gridPattern = hasHero ? document.querySelector(".grid-pattern") : null;
  const holeReady = !!(gridPattern && gridPattern.dataset.holeX1);

  // FLIP mesuré : gros logo (droite) -> slot logo du header (haut-gauche)
  const s = bigLogo.getBoundingClientRect();
  // La cible du FLIP est mesurée au lancement du vol (valeurs fonction GSAP),
  // pas ici : sur mobile la barre d'URL du navigateur se rétracte pendant le
  // fade + hold, ce qui déplace le logo header. Mesurer trop tôt fait atterrir
  // le gros logo décalé (trop bas), d'où le saut visible au raccord.
  const measureFlip = () => {
    const sNow = bigLogo.getBoundingClientRect();
    const elNow = headLogo.getBoundingClientRect();
    return {
      scale: elNow.width / sNow.width,
      x: elNow.left + elNow.width / 2 - (sNow.left + sNow.width / 2),
      y: elNow.top + elNow.height / 2 - (sNow.top + sNow.height / 2),
    };
  };
  gsap.set(bigLogo, { transformOrigin: "50% 50%" });

  if (holeReady) {
    Animations.setHole(false); // le canvas rend tout pendant l'intro
    // Etat Loader : grille floue + trou fermé (aire nulle au centre du gros logo)
    const cx = s.left + s.width / 2;
    const cy = s.top + s.height / 2;
    gsap.set(gridPattern, {
      filter: "blur(8px)",
      "--hx0": cx + "px",
      "--hy0": cy + "px",
      "--hx1": cx + "px",
      "--hy1": cy + "px",
    });
  }

  const tl = gsap.timeline({
    onComplete: () => {
      if (holeReady) Animations.setHole(true);
      Scroller.start();
      preloader.remove();
      document.body.classList.add("loaded");
    },
  });

  // 1. On révèle l'état Loader (image + grille floue + gros logo) derrière le fond
  tl.to(preloader, {
    backgroundColor: "rgba(12, 12, 12, 0)",
    duration: 0.5,
    ease: "power2.out",
  });

  // 2. On tient l'état Loader un instant (durée mini garantie, même image en
  //    cache), puis PHASE 1 : le gros logo rejoint le header, la grille se net
  //    et le trou (zone vide) se dessine vers le haut-gauche.
  tl.to({}, { duration: 0.8 });

  if (isDesktop) {
    tl.to(bigLogo, {
      x: () => measureFlip().x,
      y: () => measureFlip().y,
      scale: () => measureFlip().scale,
      duration: 1.3,
      ease: "expo.inOut",
    });
  } else {
    tl.to({}, { duration: 1.3 });
  }

  if (holeReady) {
    tl.to(
      gridPattern,
      {
        filter: "blur(0px)",
        "--hx0": "0px",
        "--hy0": "0px",
        "--hx1": gridPattern.dataset.holeX1 + "px",
        "--hy1": gridPattern.dataset.holeY1 + "px",
        duration: 1.3,
        ease: "expo.inOut",
      },
      "<"
    );
  }

  if (isDesktop) {
    // Handoff : logo du preloader -> logo du header (crossfade avec recouvrement)
    tl.to(headLogo, { opacity: 1, duration: 0.25 }, ">-0.2").to(
      bigLogo,
      { opacity: 0, duration: 0.2 },
      "<0.05"
    );
  }
  // Mobile : le gros logo est masqué en CSS (display: none), le logo header
  // apparaît plus tard avec le menu (phase 2).

  // 3. PHASE 2 : le texte apparaît dans la zone vide (reveal forcé) + le menu descend
  if (heroText.length) {
    tl.add(() => {
      gsap.set(heroText, { opacity: 1 });
      Animations.revealText(heroText, true);
    }, ">");
  }

  if (breadcrumb.length) {
    tl.to(breadcrumb, { opacity: 1, duration: 0.8, ease: "power2.out" }, "<");
  }

  if (heroButtons.length) {
    tl.to(heroButtons, { opacity: 1, duration: 0.8, ease: "power2.out" }, "<");
  }

  if (menuWrap) {
    tl.fromTo(
      menuWrap,
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
      "<"
    );
    if (!isDesktop) {
      tl.to(headLogo, { opacity: 1, duration: 0.6, ease: "power3.out" }, "<");
    }
  }

  return tl;
};

// Dynamic Import Logic
const loadPageScript = async (namespace) => {
  try {
    const module = await import(`./pages/${namespace}.js`);
    if (module.default) module.default.init();
  } catch (e) {
    // console.log(`No specific script for ${namespace}`);
  }
};

const getNamespace = () => {
  return document.querySelector("[data-page]")?.dataset.page;
};

const sayHello = () => {
  console.log(
    "%c Made with ♥️ by Wesign %c https://www.wesign.fr/ ",
    "background: #000; color: #fff; font-size: 11px; font-weight: bold; padding: 4px 8px; border-radius: 20px;",
    "color: #fff; font-size: 11px; padding: 4px 8px; border-radius: 0 20px 20px 0; font-family: monospace;"
  );
};

// Init App
const initApp = async () => {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  sayHello();
  // 1. Initial Load
  Scroller.init();
  Menu.init();
  const animationsReady = Animations.init();
  await initPreloader();
  //document.querySelector("#preloader")?.remove();
  await animationsReady;

  const swup = new Swup({
    containers: ["#swup"],
    animationSelector: false,
    animateHistoryBrowsing: true,
    cache: true,
    plugins: [new SwupPreloadPlugin()],
  });

  const overlay = document.querySelector("#transition-overlay");
  const gridWrapper = document.querySelector(".grid-wrapper.bod");
  // const canvas = document.querySelector('.canvas-hero-wrap')

  const duration = 1.5;

  // --- HOOKS SWUP ---

  // ÉTAPE 1 : Début du clic
  swup.hooks.on("visit:start", (visit) => {
    Menu.close();
    if (visit.to.url.includes("/contact")) {
      localStorage.setItem("contact_from_url", window.location.href);
    }
  });

  // ÉTAPE 2 : Animation de sortie (Fermer le rideau)
  swup.hooks.on("animation:out:await", async () => {
    Animations.setHole(false);

    const content = document.querySelector(".section-entry .w-col-lg-8");
    const header = document.getElementById("header");

    const tl = gsap.timeline();

    tl.to(overlay, {
      opacity: 1,
      zIndex: 11,
      duration: duration,
      ease: "power2.inOut",
    }).to(
      gridWrapper,
      {
        opacity: 1,
        zIndex: 12,
        duration: duration,
        ease: "power2.inOut",
      },
      "<.2"
    );

    await tl;
  });

  // ÉTAPE 3 : Remplacement du contenu (Derrière le rideau)
  swup.hooks.on("content:replace", () => {
    const namespace = getNamespace();

    // On remonte en haut NATIVEMENT avant de relancer le scroller
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    // Reset Scroller (Destroy + Init) pour recalculer la hauteur du nouveau DOM
    // → Scroller.destroy() kill les ScrollTriggers automatiquement
    Scroller.reset();

    if (namespace) {
      loadPageScript(namespace);
    }
  });

  // ÉTAPE 4 : Animation d'entrée (Ouvrir le rideau)
  swup.hooks.on("animation:in:await", async () => {
    const content = document.querySelector(".section-entry .w-col-lg-8");
    const header = document.getElementById("header");
    const gridPattern = document.querySelector(".grid-pattern");

    Scroller.resize();
    await Animations.init();
    ScrollTrigger.refresh();

    gsap.set(gridPattern, {
      "--hx0": "0px",
      "--hy0": "0px",
      "--hx1": "0px",
      "--hy1": "0px",
    });

    const targetW = (gridPattern?.dataset.holeX1 || 0) + "px";
    const targetH = (gridPattern?.dataset.holeY1 || 0) + "px";

    const tl = gsap.timeline();

    tl.to(gridWrapper, {
      opacity: 0,
      zIndex: 0,
      duration: duration,
      ease: "power2.inOut",
    })
      .to(
        overlay,
        {
          opacity: 0,
          zIndex: -1,
          duration: duration,
          ease: "power2.inOut",
        },
        "<.2"
      )

      .to(
        gridPattern,
        {
          duration: 2,
          ease: "expo.inOut",
          "--hx1": targetW,
          "--hy1": targetH,
        },
        "<"
      )

      .from(
        content,
        {
          opacity: 0,
          duration: 1,
          ease: "power2.inOut",
        },
        "1.5"
      )

      .from(
        header,
        {
          opacity: 0,
          duration: 1,
          ease: "power2.inOut",
        },
        "<"
      );

    await tl;
    Animations.setHole(true);
  });

  // Initial load script
  const currentNamespace = getNamespace();
  if (currentNamespace) loadPageScript(currentNamespace);
};

document.addEventListener("DOMContentLoaded", initApp);
